import type { CharacterClassId, CharacterRole, CharacterRosterEntry, WeaponClass } from '../config/character-roster';
import { abilityIconUrl, abilityVisual } from '../config/ability-vfx';
import { talentCopy, effectLine, talentIconFallback } from '../config/talent-copy';
import type { TalentNodeRow } from '../net/character-api';
import { t, N_ } from '../i18n/i18n';

/**
 * The two picks in the creation wizard, with something to look at.
 *
 * User: "here when I'm creating character I want visuals of each class and
 * same with talents."
 *
 * Both steps were text rows. The class step listed four names over one line
 * of prose each; the talent step listed `nodeId.replace(/_/g, ' ')`, so the
 * first permanent decision in the game was made from the words "iron will"
 * with nothing on screen saying what it did. The talent tree in the hub was
 * rebuilt out of exactly that state in August — the wizard was simply left
 * behind, and it is the screen a player meets *first*.
 *
 * Everything drawn here already existed: painted talent icons in
 * `/assets/ui/talents`, names and flavor in `config/talent-copy.ts`, and the
 * effect computed from the server's own `field`/`perRank` by `effectLine` so
 * a rebalance changes the words without anyone editing copy. The class
 * portraits are the landing page's, keyed onto transparency so they sit on
 * the wizard's card rather than carrying their own background.
 *
 * Pure string builders, like the rest of `ui/`.
 */

const CLASS_PORTRAIT_BASE = '/assets/ui/classes';
const TALENT_ICON_BASE = '/assets/ui/talents';

export function classPortraitSrc(id: CharacterClassId): string {
  return `${CLASS_PORTRAIT_BASE}/${id}.webp`;
}

/**
 * What a role is *for*, in a party of three.
 *
 * Shown because the weapon line alone could not tell a knight from a druid —
 * both are MEDIUM, so both read "Balanced — the middle ground on reach, swing
 * speed, and stamina cost", word for word. Two of the four classes were
 * described identically on the one screen where the choice is permanent.
 */
export const ROLE_COPY: Record<CharacterRole, string> = {
  TANK: N_('Holds the front'),
  HEALER: N_('Keeps the party up'),
  DPS: N_('Deals the damage'),
};

const ROLE_TONE: Record<CharacterRole, string> = {
  TANK: 'text-gold',
  HEALER: 'text-success',
  DPS: 'text-error',
};

/** One copy, read by the class step here and by the wizard's stats step. */
export const WEAPON_IDENTITY: Record<WeaponClass, string> = {
  LIGHT: N_('Light & fast — quick swings, shorter reach, the lowest stamina cost per hit.'),
  MEDIUM: N_('Balanced — the middle ground on reach, swing speed, and stamina cost.'),
  BLUNT: N_('Blunt — the least damage of any weapon, and the only impact that staggers plate.'),
  HEAVY: N_('Heavy & slow — long reach and hard-hitting swings, at a higher stamina cost.'),
  REACH: N_('Reach — the longest strike in the game through the narrowest arc, and a long recovery.'),
};

/**
 * Class names as shown. Lower-case on purpose: every place draws them under
 * CSS `capitalize`, so a translation is written lower-case too.
 */
export const CLASS_NAME: Record<CharacterClassId, string> = {
  rogue: N_('rogue'),
  knight: N_('knight'),
  barbarian: N_('barbarian'),
  druid: N_('druid'),
};

/** The class's display name in the active language (lower-case; capitalise with CSS). */
export function classDisplayName(id: string): string {
  const key = CLASS_NAME[id as CharacterClassId];
  return key ? t(key) : id;
}

function esc(text: string): string {
  return text.replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string
  ));
}

/**
 * One class, as a card.
 *
 * The portrait leads because it is the fastest thing to tell apart, and the
 * role sits directly under the name because it is the fastest thing to choose
 * by. `loading="eager"`: four images on the first step of a wizard, all above
 * the fold, where lazy loading would only add a flash of nothing.
 */
export function classCardMarkup(entry: CharacterRosterEntry, selected: boolean): string {
  const border = selected ? 'border-gold bg-gold/10' : 'border-border bg-surface-2 hover:border-gold/50';
  return `
    <button type="button" data-pick-class="${entry.id}" aria-pressed="${selected}"
      class="text-left p-3 rounded border transition-colors flex flex-col items-center gap-1.5 ${border}">
      <img src="${classPortraitSrc(entry.id)}" alt="" aria-hidden="true" draggable="false"
        width="256" height="256" loading="eager" decoding="async"
        class="w-20 h-20 sm:w-24 sm:h-24 object-contain select-none" />
      <span class="font-heading text-text tracking-wide capitalize">${esc(classDisplayName(entry.id))}</span>
      <span class="eyebrow eyebrow-dim ${ROLE_TONE[entry.role]}">${esc(t(ROLE_COPY[entry.role]))}</span>
    </button>`;
}

/** The four cards, plus the weapon line for whichever is selected. */
export function classStepMarkup(roster: readonly CharacterRosterEntry[], selected: CharacterClassId | null): string {
  const chosen = roster.find((c) => c.id === selected);
  return `
    <p class="text-[13px] text-text-dim">${esc(t('Choose a class. This is permanent — a Character can never change class later.'))}</p>
    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
      ${roster.map((c) => classCardMarkup(c, c.id === selected)).join('')}
    </div>
    <p class="text-[12px] text-text-dim min-h-[2.5em]">${
      chosen ? esc(t(WEAPON_IDENTITY[chosen.weaponClass])) : esc(t('Pick one to see how it fights.'))
    }</p>`;
}

/**
 * One tier-1 talent, as a row.
 *
 * Icon, real name, what it actually does, and a line of voice — the same four
 * things the hub's tree shows, because a player choosing their first point
 * deserves the same information as one choosing their twentieth.
 *
 * The effect is computed at rank 1, which is what taking it here grants.
 */
export function talentOptionMarkup(
  node: TalentNodeRow,
  characterClass: CharacterClassId,
  selected: boolean,
): string {
  const copy = talentCopy(node.id);
  const effect = effectLine(node.field, node.perRank, 1, characterClass);
  const border = selected ? 'border-gold bg-gold/10' : 'border-border bg-surface-2 hover:border-gold/50';
  return `
    <button type="button" data-pick-talent="${esc(node.id)}" aria-pressed="${selected}"
      class="text-left p-2.5 rounded border transition-colors flex items-center gap-3 ${border}">
      <img src="${TALENT_ICON_BASE}/${esc(node.id)}.webp"
        data-fallback="${TALENT_ICON_BASE}/${esc(talentIconFallback(node.id, node.field))}.webp"
        alt="" aria-hidden="true" draggable="false"
        width="128" height="128" loading="eager" decoding="async"
        class="w-12 h-12 shrink-0 rounded object-contain select-none" />
      <span class="min-w-0">
        <span class="block font-heading text-text tracking-wide">${esc(t(copy.name))}</span>
        ${effect ? `<span class="block text-[12px] text-gold tabular-nums">${esc(effect)}</span>` : ''}
        <span class="block text-[12px] text-text-dim italic">${esc(t(copy.flavor))}</span>
      </span>
    </button>`;
}


/**
 * The abilities step, matched to the two steps beside it.
 *
 * It already drew the ability plate — the same art the action bar draws in
 * the run, added in August so the first sight of Brace was not mid-dungeon.
 * What it did not carry was the class. Three comparison rows named ROGUE,
 * BARBARIAN and DRUID in text while the step before them showed portraits,
 * so the one screen that puts four classes side by side was the one screen
 * that did not picture them.
 *
 * Now every row wears both: the class portrait it was chosen by, and the
 * plate that class casts. Yours is a wide card at the top; the other three
 * are a grid, the same grid the class step uses, because they are being
 * compared rather than read in order.
 */
export interface AbilityInfo {
  id: string;
  name: string;
  blurb: string;
}

function abilityPlate(id: string, size: string): string {
  return `<img src="${abilityIconUrl(abilityVisual(id).icon)}" alt="" aria-hidden="true"
    draggable="false" width="128" height="128" loading="eager" decoding="async"
    class="${size} rounded shrink-0 object-cover select-none" />`;
}

function portrait(id: CharacterClassId, size: string): string {
  return `<img src="${classPortraitSrc(id)}" alt="" aria-hidden="true" draggable="false"
    width="256" height="256" loading="eager" decoding="async"
    class="${size} shrink-0 object-contain select-none" />`;
}

export function abilityStepMarkup(
  mine: CharacterClassId,
  others: readonly CharacterClassId[],
  info: Record<CharacterClassId, AbilityInfo>,
): string {
  const others3 = others.map((id) => `
    <div class="frame-inset p-2.5 flex flex-col items-center gap-1 text-center">
      <span class="relative">
        ${portrait(id, 'w-14 h-14')}
        <span class="absolute -bottom-1 -right-1">${abilityPlate(info[id].id, 'w-8 h-8')}</span>
      </span>
      <span class="eyebrow eyebrow-dim capitalize mt-0.5">${esc(classDisplayName(id))}</span>
      <span class="font-heading text-text text-sm">${esc(info[id].name)}</span>
      <span class="text-[12px] text-text-dim">${esc(info[id].blurb)}</span>
    </div>`).join('');

  return `
    <div class="frame-inset border-gold/40 p-3 flex gap-3 items-center">
      ${portrait(mine, 'w-16 h-16 sm:w-20 sm:h-20')}
      ${abilityPlate(info[mine].id, 'w-14 h-14')}
      <span class="min-w-0">
        <span class="block eyebrow eyebrow-dim">${t('Your ability — {class}', { class: `<span class="capitalize">${esc(classDisplayName(mine))}</span>` })}</span>
        <span class="block font-heading text-gold text-base mt-1">${esc(info[mine].name)}</span>
        <span class="block text-[13px] text-text mt-1">${esc(info[mine].blurb)}</span>
      </span>
    </div>
    <p class="text-[12px] text-text-dim mt-1">${esc(t('For comparison, here’s what the other classes get:'))}</p>
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">${others3}</div>`;
}
